import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { Switch } from "apps/user/common/components/fields/inputs";
import { SET_OWN_FEATURE_FLAG } from "utility/graphQl/mutation";
import { TOGGLEABLE_FEATURE_FLAGS } from "utility/graphQl/query";
import { SetOwnFeatureFlag } from "apps/user/learner/api/types";
import useToggleableFeatureFlags from "apps/user/learner/hooks/use-toggleable-feature-flags";
import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";

import styles from "./styles.module.scss";

interface SetOwnFeatureFlagResult {
  setOwnFeatureFlag: SetOwnFeatureFlag;
}

const FeatureFlags = () => {
  const { t } = useTranslation(["settings"]);
  const { flags } = useToggleableFeatureFlags();
  const [setOwnFeatureFlag] = useMutation<SetOwnFeatureFlagResult>(
    SET_OWN_FEATURE_FLAG
  );
  const [errorMessage, setErrorMessage] = useState("");

  if (flags.length === 0) {
    return null;
  }

  const sortedFlags = [...flags].sort((a, b) => a.name.localeCompare(b.name));

  const handleChange = (name: string, enabled: boolean) => {
    setOwnFeatureFlag({
      variables: { name, enabled },
      optimisticResponse: {
        setOwnFeatureFlag: {
          __typename: "SetOwnFeatureFlag",
          ok: true,
          flags: sortedFlags.map((flag) =>
            flag.name === name ? { ...flag, enabled } : flag
          ),
        },
      },
      update: (cache, { data }) => {
        const updated = data?.setOwnFeatureFlag?.flags;
        if (!updated) return;
        cache.writeQuery({
          query: TOGGLEABLE_FEATURE_FLAGS,
          data: { toggleableFeatureFlags: updated },
        });
      },
    })
      .then(({ data }) => {
        if (data?.setOwnFeatureFlag && !data.setOwnFeatureFlag.ok) {
          setErrorMessage("Failed to update flag. Please try again.");
          logError(
            "Feature flag toggle rejected",
            new Error(`setOwnFeatureFlag returned ok=false for ${name}`)
          );
          return;
        }
        setErrorMessage("");
      })
      .catch((error) => {
        setErrorMessage(error.message);
        logError("Error in feature flag toggle", error);
      });
  };

  return (
    <div className={styles["feature-flags"]}>
      <h1 className={styles["feature-flags-title"]}>
        {t("settings:featureFlags.sectionTitle")}
      </h1>
      <div>
        {sortedFlags.map((flag) => (
          <Switch
            key={flag.name}
            id={flag.name}
            label={flag.name}
            checked={flag.enabled}
            onChange={handleChange}
            disabled={false}
          />
        ))}
        {errorMessage && (
          <p className={styles["error-message"]}>{errorMessage}</p>
        )}
      </div>
    </div>
  );
};

export default FeatureFlags;
